import React from 'react';
import { calculateItemTotal } from '../utils/helpers';
import AutocompleteSelect from './AutocompleteSelect';
import CustomDropdown from './CustomDropdown';
import {
  Package,
  Trash2,
  Plus,

} from 'lucide-react';

const InvoiceItemsTable = ({ items, onItemsChange, readOnly = false, products = [], originalItems = [] }) => {
  // Merge items for read-only view
  const itemsToDisplay = React.useMemo(() => {
    if (!readOnly) return items;

    const consolidatedItemsMap = new Map();
    items.forEach(item => {
      const prodId = item.product ? (typeof item.product === 'object' ? item.product._id : item.product) : null;
      const selectedProduct = products?.find(p => p._id === prodId) || (typeof item.product === 'object' ? item.product : null);
      const isService = selectedProduct?.type === 'service';
      const resolvedUnit = item.unit || selectedProduct?.unit || (typeof item.product === 'object' ? item.product?.unit : '') || 'pcs';
      const key = `${(item.productName || '').trim().toLowerCase()}_${(item.description || '').trim().toLowerCase()}_${Number(item.rate).toFixed(2)}_${(item.hsnSac || '').trim().toLowerCase()}_${item.gstRate}_${resolvedUnit}`;

      if (consolidatedItemsMap.has(key)) {
        const existing = consolidatedItemsMap.get(key);
        if (isService) {
          existing.amount = (Number(existing.amount) || 0) + (Number(item.amount) || 0);
          // Keep rate same as amount for service display
          existing.rate = (Number(existing.rate) || 0) + (Number(item.rate) || 0);
        } else {
          existing.quantity = (Number(existing.quantity) || 0) + (Number(item.quantity) || 0);
        }
        existing.total = (Number(existing.total) || 0) + (Number(calculateItemTotal(item).total) || 0);
      } else {
        consolidatedItemsMap.set(key, { ...item, unit: resolvedUnit, total: calculateItemTotal(item).total });
      }
    });
    return Array.from(consolidatedItemsMap.values());
  }, [items, readOnly, products]);

  const addItem = () => {
    onItemsChange([
      ...items,
      {
        product: '',
        productName: '',
        description: '',
        hsnSac: '',
        quantity: 1,
        unit: 'pcs',
        rate: 0,
        gstRate: 18
      }
    ]);
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    onItemsChange(newItems);
  };

  const handleProductChange = (index, productId) => {
    const newItems = [...items];
    newItems[index].product = productId;

    if (productId) {
      const selectedProduct = products.find(p => p._id === productId);
      if (selectedProduct) {
        // Auto-fill all product details
        newItems[index].productName = selectedProduct.name || '';
        newItems[index].description = selectedProduct.description || selectedProduct.name || '';
        newItems[index].hsnSac = selectedProduct.hsnSac || '';
        newItems[index].unit = selectedProduct.unit || 'pcs';
        newItems[index].rate = selectedProduct.rate || 0;
        newItems[index].gstRate = selectedProduct.gstRate ?? 18;
      }
    }

    onItemsChange(newItems);
  };

  const removeItem = (index) => {
    onItemsChange(items.filter((_, i) => i !== index));
  };

  return (
    <div className="invoice-items">
      <div className="table-container" style={{ overflow: 'visible' }}>
        <table className="invoice-items-table" style={{ width: '100%' }}>
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5' }}>
              <th style={{ width: '4%', padding: '12px 8px' }}>#</th>
              {!readOnly && (
                <th style={{ width: '15%', padding: '12px 8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Package size={16} /> Product
                  </div>
                </th>
              )}
              <th style={{ width: readOnly ? '28%' : '22%', padding: '12px 8px' }}>Description</th>
              <th style={{ width: '10%', padding: '12px 8px' }}>HSN/SAC</th>
              <th style={{ width: '8%', padding: '12px 8px' }}>Qty</th>
              <th style={{ width: '8%', padding: '12px 8px' }}>Unit</th>
              <th style={{ width: '10%', padding: '12px 8px' }}>Rate (₹)</th>
              <th style={{ width: '8%', padding: '12px 8px' }}>GST %</th>
              <th style={{ width: '12%', padding: '12px 8px', textAlign: 'right' }}>Total</th>
              {!readOnly && <th style={{ width: '7%', padding: '12px 8px' }}>Action</th>}
            </tr>
          </thead>
          <tbody>
            {itemsToDisplay.map((item, index) => {
              const totals = calculateItemTotal(item);
              const prodId = item.product ? (typeof item.product === 'object' ? item.product._id : item.product) : null;
              const selectedProduct = products?.find(p => p._id === prodId) || (typeof item.product === 'object' ? item.product : null);
              const isService = selectedProduct?.type === 'service';
              let maxQuantity = (selectedProduct && !isService) ? selectedProduct.stock : undefined;

              if (maxQuantity !== undefined && !readOnly) {
                // Add back total original quantity for this product (if editing)
                if (originalItems.length > 0) {
                  const originalQuantity = originalItems.reduce((sum, orig) => {
                    if ((orig.product && orig.product === item.product) || (!orig.product && orig.description === item.description && orig.hsnSac === item.hsnSac)) {
                      return sum + (Number(orig.quantity) || 0);
                    }
                    return sum;
                  }, 0);
                  maxQuantity += originalQuantity;
                }

                // Subtract the quantity already entered in OTHER rows for this exact same product
                const otherRowsQuantity = items.reduce((sum, otherItem, otherIndex) => {
                  if (otherIndex !== index && otherItem.product === item.product) {
                    return sum + (Number(otherItem.quantity) || 0);
                  }
                  return sum;
                }, 0);

                maxQuantity -= otherRowsQuantity;
                maxQuantity = Math.max(0, maxQuantity); // Don't let it go below zero
              }
              return (
                <tr key={index} style={{ borderBottom: '1px solid #eee', position: 'relative', zIndex: 100 - index }}>
                  <td style={{ padding: '10px 8px', textAlign: 'center', fontWeight: '600' }}>{index + 1}</td>
                  {!readOnly && (
                    <td style={{ padding: '10px 8px' }}>
                      <AutocompleteSelect
                        value={item.product || ''}
                        onChange={(value) => handleProductChange(index, value)}
                        options={products}
                        placeholder="Search product..."
                        displayField="name"
                        valueField="_id"
                        className="form-control"
                        optionDisabled={(product) => {
                          if (product.type === 'service') return false;
                          const usedQty = items.reduce((sum, it, i) => (i !== index && it.product === product._id) ? sum + (Number(it.quantity) || 0) : sum, 0);
                          return (product.stock - usedQty) <= 0;
                        }}
                        renderOption={(product) => {
                          const isItemService = product.type === 'service';
                          const usedQty = items.reduce((sum, it, i) => (i !== index && it.product === product._id) ? sum + (Number(it.quantity) || 0) : sum, 0);
                          let availableStock = product.stock - usedQty;

                          // If editing, add back original quantity for this product
                          if (originalItems.length > 0) {
                            const originalQty = originalItems.reduce((sum, orig) => {
                              if (orig.product === product._id) return sum + (Number(orig.quantity) || 0);
                              return sum;
                            }, 0);
                            availableStock += originalQty;
                          }

                          availableStock = Math.max(0, availableStock);

                          return (
                            <div>
                              <div style={{ fontWeight: '600', fontSize: '13px', display: 'flex', justifyContent: 'space-between' }}>
                                <span>{product.name} <span style={{ fontSize: '11px', opacity: 0.8 }}>(#{product.productId})</span></span>
                                {!isItemService && <span style={{ color: availableStock > 0 ? '#2e7d32' : '#c62828' }}>Stock: {availableStock}</span>}
                              </div>
                              <span>{product.description}</span>
                              <div style={{ fontSize: '11px', color: '#666' }}>
                                HSN: {product.hsnSac} | Rate: ₹{product.rate} | GST: {product.gstRate}%
                              </div>
                              {!isItemService && availableStock <= 0 && (
                                <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '2px', fontStyle: 'italic' }}>
                                  Out of Stock
                                </div>
                              )}
                            </div>
                          );
                        }}
                      />
                    </td>
                  )}
                  <td style={{ padding: '10px 8px' }}>
                    {readOnly ? (
                      <div>
                        {item.productName && (
                          <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>
                            {item.productName}
                          </div>
                        )}
                        {item.description && (
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {item.description}
                          </div>
                        )}
                        {!item.productName && !item.description && '-'}
                      </div>
                    ) : (
                      <div>
                        <input
                          type="text"
                          value={item.productName || ''}
                          onChange={(e) => updateItem(index, 'productName', e.target.value)}
                          placeholder="Product name"
                          style={{ width: '100%', padding: '6px', fontSize: '14px', marginBottom: '4px', fontWeight: '600' }}
                        />
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => updateItem(index, 'description', e.target.value)}
                          placeholder="Description (optional)"
                          style={{ width: '100%', padding: '6px', fontSize: '12px' }}
                        />
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '10px 8px' }}>
                    {readOnly ? (
                      item.hsnSac
                    ) : (
                      <input
                        type="text"
                        value={item.hsnSac}
                        onChange={(e) => updateItem(index, 'hsnSac', e.target.value)}
                        placeholder="HSN"
                        required
                        style={{ width: '100%', padding: '6px', fontSize: '14px' }}
                      />
                    )}
                  </td>
                  <td style={{ padding: '10px 8px' }}>
                    {isService ? (
                      <div style={{ textAlign: 'center', color: '#999' }}>-</div>
                    ) : readOnly ? (
                      item.quantity
                    ) : (
                      <div>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => {
                            let val = parseFloat(e.target.value);
                            if (isNaN(val)) val = 0;
                            if (maxQuantity !== undefined && val > maxQuantity) {
                              val = maxQuantity;
                              alert('Maximum available stock is ' + maxQuantity);
                            }
                            updateItem(index, 'quantity', val);
                          }}
                          min="0"
                          max={maxQuantity}
                          step="0.01"
                          required
                          style={{ width: '100%', padding: '6px', fontSize: '14px', borderColor: maxQuantity !== undefined && item.quantity >= maxQuantity ? '#f59e0b' : '' }}
                        />
                        {maxQuantity !== undefined && (
                          <div style={{
                            fontSize: '11px',
                            color: item.quantity >= maxQuantity ? 'var(--danger, #dc2626)' : 'var(--accent-dark, #066478)',
                            marginTop: '6px',
                            background: item.quantity >= maxQuantity ? 'var(--danger-bg, #fee2e2)' : 'var(--info-bg, #e6f6f8)',
                            padding: '4px 6px',
                            borderRadius: '6px',
                            fontWeight: '600',
                            textAlign: 'center',
                            border: item.quantity >= maxQuantity ? '1px solid #fca5a5' : '1px solid var(--accent-mid, #c3eaef)',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                            display: 'block',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}>
                            Max: {maxQuantity}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '10px 8px' }}>
                    {isService ? (
                      <div style={{ textAlign: 'center', color: '#999' }}>-</div>
                    ) : readOnly ? (
                      item.unit || selectedProduct?.unit || (typeof item.product === 'object' ? item.product?.unit : '') || 'pcs'
                    ) : (
                      <input
                        type="text"
                        value={item.unit || 'pcs'}
                        onChange={(e) => updateItem(index, 'unit', e.target.value)}
                        placeholder="pcs"
                        style={{ width: '100%', padding: '6px', fontSize: '14px' }}
                      />
                    )}
                  </td>
                  <td style={{ padding: '10px 8px' }}>
                    {readOnly ? (
                      `₹${item.rate.toFixed(2)}`
                    ) : (
                      <input
                        type="number"
                        value={item.rate}
                        onChange={(e) => updateItem(index, 'rate', parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                        required
                        style={{ width: '100%', padding: '6px', fontSize: '14px' }}
                      />
                    )}
                  </td>
                  <td style={{ padding: '10px 8px' }}>
                    {readOnly ? (
                      `${item.gstRate}%`
                    ) : (
                      <select
                        value={item.gstRate}
                        onChange={(e) => updateItem(index, 'gstRate', parseFloat(e.target.value))}
                        required
                        disabled={true}
                        style={{ width: '100%', padding: '6px', fontSize: '14px', backgroundColor: '#f3f4f6', cursor: 'not-allowed', color: '#666', border: '1px solid #d1d5db', borderRadius: '4px' }}
                      >
                        <option value="0">0%</option>
                        <option value="5">5%</option>
                        <option value="12">12%</option>
                        <option value="18">18%</option>
                        <option value="28">28%</option>
                      </select>
                    )}
                  </td>
                  <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '600', fontSize: '15px' }}>
                    ₹{totals.total.toFixed(2)}
                  </td>
                  {!readOnly && (
                    <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                      <button
                        type="button"
                        className="btn btn-danger btn-small"
                        onClick={() => removeItem(index)}
                        disabled={items.length === 1}
                        style={{
                          padding: '6px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '32px',
                          minHeight: '32px'
                        }}
                        title="Remove item"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!readOnly && (
        <div style={{ marginTop: '15px', textAlign: 'left', position: 'relative', zIndex: 1 }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={addItem}
            style={{
              fontSize: '14px',
              padding: '8px 16px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Plus size={16} /> Add Item
          </button>
        </div>
      )}
    </div>
  );
};

export default InvoiceItemsTable;
